import type { CrewJobPayUnit } from '../types/crewJobs';
import type { ContractorAgreementFields } from '../types/contractorOnboard';
import type { JobOfferSection } from '../types/hiring';
import { LCTF_DEFAULT_CEO_NAME, LCTF_DEFAULT_CEO_TITLE, LCTF_OFFER_COMPANY, LCTF_OFFER_LEGAL } from './jobOfferLetter';

export function contractorAgreementFullName(
  fields: Pick<ContractorAgreementFields, 'contractorFirstName' | 'contractorLastName'>,
): string {
  return `${fields.contractorFirstName || ''} ${fields.contractorLastName || ''}`.replace(/\s+/g, ' ').trim();
}

export function formatContractorPay(fields: Pick<ContractorAgreementFields, 'payAmount' | 'payUnit'>): string {
  const amount = Number(fields.payAmount) || 0;
  const money = amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: amount % 1 ? 2 : 0,
  });
  return fields.payUnit === 'day' ? `${money} per day` : `${money} per hour`;
}

export function defaultContractorAgreementFields(input: {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle?: string | null;
  location?: string | null;
  payAmount?: number | null;
  payUnit?: CrewJobPayUnit | null;
  scope?: string | null;
}): ContractorAgreementFields {
  return {
    contractorFirstName: input.firstName.trim(),
    contractorLastName: input.lastName.trim(),
    contractorEmail: input.email.trim().toLowerCase(),
    jobTitle: (input.jobTitle || 'Timber frame raise crew').trim(),
    scope: (input.scope || '').trim() || defaultContractorScope(input.jobTitle || 'timber frame raise'),
    payAmount: Number(input.payAmount) || 0,
    payUnit: input.payUnit === 'day' ? 'day' : 'hour',
    location: (input.location || '').trim(),
    ceoName: LCTF_DEFAULT_CEO_NAME,
    ceoTitle: LCTF_DEFAULT_CEO_TITLE,
  };
}

function isGeneratedContractorScope(scope: string, jobTitle: string): boolean {
  const normalized = normalizeContractorScopeTools(scope).trim();
  if (!normalized) return true;
  const titles = [jobTitle, 'timber frame raise', 'Timber frame raise crew'];
  return titles.some(title => normalized === defaultContractorScope(title).trim());
}

/**
 * Draft agreements are stored per person, not per raise. Overlay the currently
 * selected crew job so Work / raise, location, and generated scope follow the
 * job on screen instead of lastJobTitle from an earlier raise.
 */
export function overlaySelectedJobOnAgreementFields(
  fields: ContractorAgreementFields,
  job: {
    title?: string | null;
    location?: string | null;
    payAmount?: number | null;
    payUnit?: CrewJobPayUnit | null;
  } | null | undefined,
): ContractorAgreementFields {
  if (!job) return fields;
  const nextTitle = (job.title || '').trim();
  if (!nextTitle) return fields;
  const nextLocation = (job.location || '').trim();
  const nextPay = Number(job.payAmount) || 0;
  const replaceScope = isGeneratedContractorScope(fields.scope, fields.jobTitle);
  return {
    ...fields,
    jobTitle: nextTitle,
    location: nextLocation,
    scope: replaceScope ? defaultContractorScope(nextTitle) : normalizeContractorScopeTools(fields.scope || ''),
    payAmount: nextPay > 0 ? nextPay : fields.payAmount,
    payUnit: job.payUnit === 'day' || job.payUnit === 'hour' ? job.payUnit : fields.payUnit,
  };
}

export function defaultContractorScope(jobTitle: string): string {
  return `Provide independent timber-frame raise labor for ${jobTitle} and related on-site work as assigned for this engagement. The Contractor may supply their own tools; otherwise Leiper's Creek Timber Frames will provide the tools needed for the work. Duties and expectations for this engagement will be set by an LCTF team member, subject to site safety and schedule.`;
}

/** Upgrade legacy scope wording (tools / manner-and-means). */
export function normalizeContractorScopeTools(scope: string): string {
  return scope
    .replace(
      /The contractor supplies their own tools and controls the manner and means of performing the work, subject to site safety and schedule\./gi,
      `The Contractor may supply their own tools; otherwise Leiper's Creek Timber Frames will provide the tools needed for the work. Duties and expectations for this engagement will be set by an LCTF team member, subject to site safety and schedule.`,
    )
    .replace(
      /The Contractor may supply their own tools; otherwise Leiper's Creek Timber Frames will provide the tools needed for the work\. The Contractor controls the manner and means of performing the work, subject to site safety and schedule\./gi,
      `The Contractor may supply their own tools; otherwise Leiper's Creek Timber Frames will provide the tools needed for the work. Duties and expectations for this engagement will be set by an LCTF team member, subject to site safety and schedule.`,
    );
}

export function assembleContractorAgreementSections(fields: ContractorAgreementFields): JobOfferSection[] {
  const name = contractorAgreementFullName(fields) || 'the Contractor';
  const pay = formatContractorPay(fields);
  const location = fields.location ? ` Work is expected at ${fields.location}.` : '';
  const scope = normalizeContractorScopeTools(fields.scope);
  const dayRateTerms = fields.payUnit === 'day'
    ? ` This is a day rate based on a typical 10-hour workday; a workday may be as short as 6 hours or as long as 12 hours for the same day rate.`
    : '';
  return [
    {
      id: 'letterhead',
      heading: LCTF_OFFER_COMPANY,
      body: `${LCTF_OFFER_LEGAL}\nIndependent Contractor Agreement`,
    },
    {
      id: 'parties',
      heading: 'Parties',
      body: `This Independent Contractor Agreement is between ${LCTF_OFFER_LEGAL} ("Company") and ${name} ("Contractor").`,
    },
    {
      id: 'status',
      heading: 'Independent contractor status',
      body: `${name} is an independent contractor, not an employee. This is a 1099 engagement. The Company will not withhold income taxes, Social Security, or Medicare, and will not provide W-2 wages, employee benefits, paid time off, unemployment insurance, or workers' compensation coverage associated with employment. The Contractor is solely responsible for all taxes arising from amounts paid under this agreement.`,
    },
    {
      id: 'scope',
      heading: 'Scope of work',
      body: `${scope}${location}`,
    },
    {
      id: 'pay',
      heading: 'Compensation',
      body: `The Company will pay the Contractor ${pay} for accepted work on this engagement.${dayRateTerms} The Company will cover necessary travel expenses and will pay for travel time. Payment is for services rendered as an independent contractor and is not a salary, hourly employee wage, or overtime-eligible W-2 rate.`,
    },
    {
      id: 'no-employment',
      heading: 'No employment terms',
      body: 'This agreement does not create at-will employment, an offer of employment, employee benefits, a non-compete, or any W-2 payroll position. Either party may end this engagement by written notice. Outstanding accepted work remains payable.',
    },
    {
      id: 'ack',
      heading: 'Acknowledgment',
      body: `By signing, ${name} confirms they are a self-employed contractor, have provided a current Form W-9 and photo identification, and agree to the terms above.`,
    },
  ];
}

export function namesMatchContractor(
  typed: string,
  person: Pick<ContractorAgreementFields, 'contractorFirstName' | 'contractorLastName'>,
): boolean {
  const normalize = (value: string) => value.replace(/\s+/g, ' ').trim().toLowerCase();
  return normalize(typed) === normalize(contractorAgreementFullName(person));
}
